import React from "react";
import { createPortal } from "react-dom";
import { FaMicrophone } from "react-icons/fa";
import { getDictationLanguageLabel } from "../../../constants/languages";

const POPOVER_SHELL_BASE_CLASS =
  "fixed z-[99999] right-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 flex items-start gap-3 w-[340px] max-w-[calc(100vw-1rem)] sm:w-[360px]";

const POPOVER_POSITION_CLASS = {
  default: "top-1/2 -translate-y-1/2",
  radical: "top-1/3 -translate-y-1/2",
};

const STOP_BUTTON_CLASS =
  "bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white px-3 py-1.5 rounded-full text-sm font-semibold transition-colors cursor-pointer leading-none whitespace-nowrap";

function getModePillClass(isCommandMode) {
  return `inline-flex items-center px-2.5 py-1.5 rounded-full text-[11px] font-semibold border flex-shrink-0 leading-none whitespace-nowrap ${
    isCommandMode
      ? "bg-yellow-100 text-yellow-900 dark:bg-yellow-200 dark:text-yellow-900 border-yellow-200 dark:border-yellow-200"
      : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100 border-gray-200 dark:border-gray-600"
  }`;
}

function ModeHotkeysTooltip({ id, title, children }) {
  return (
    <div
      id={id}
      role="tooltip"
      className="pointer-events-none absolute z-[60] -top-2 right-0 -translate-y-full opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150"
      aria-hidden="true"
    >
      <div className="relative px-3 py-2 bg-gray-900 text-white rounded-lg shadow-lg text-[11px] whitespace-nowrap">
        <div className="font-semibold mb-1">{title}</div>
        <div className="leading-snug">{children}</div>
        <div className="absolute right-3 -bottom-1 w-3 h-3 bg-gray-900 transform rotate-45" />
      </div>
    </div>
  );
}

function EditorHotkeysTooltip({ id }) {
  return (
    <ModeHotkeysTooltip id={id} title="Hotkeys">
      <div>
        <span className="font-mono">F3</span> = Dictation (text + commands)
      </div>
      <div className="mt-0.5">
        <span className="font-mono">Shift+F3</span> = Command mode (commands only)
      </div>
      <div className="mt-0.5">
        <span className="font-mono">F2</span> = Unified mic (routes by last focus)
      </div>
    </ModeHotkeysTooltip>
  );
}

function SidebarHotkeysTooltip({ id }) {
  return (
    <ModeHotkeysTooltip id={id} title="Sidebar mic">
      <div>
        <span className="font-mono">F2</span> = Toggle mic (routes by last focus)
      </div>
      <div className="mt-0.5">Terminal = Macro command mode (commands only)</div>
    </ModeHotkeysTooltip>
  );
}

/**
 * Shared recording popover shell for editor and sidebar mics.
 */
const RecordingMicPopover = ({
  isActive,
  streamingTranscript = "",
  dictationLanguage = "en-US",
  isCommandMode = false,
  onStop,
  isRadicalAuth = false,
  variant = "editor",
}) => {
  if (!isActive) return null;

  const languageLabel = getDictationLanguageLabel(dictationLanguage);
  const transcriptText = (streamingTranscript || "").trim();
  const modeLabel = isCommandMode ? "COMMAND" : "DICTATION";
  const helpText = isCommandMode
    ? "Speak a command (no free text insertion)"
    : "Speak text or a command";
  const tooltipId =
    variant === "sidebar"
      ? "sidebar-mic-mode-hotkeys-tooltip"
      : "mic-mode-hotkeys-tooltip";

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-label={`Voice input active in ${languageLabel}`}
      className={`${POPOVER_SHELL_BASE_CLASS} ${
        isRadicalAuth ? POPOVER_POSITION_CLASS.radical : POPOVER_POSITION_CLASS.default
      }`}
    >
      {!isRadicalAuth && (
        <div className="relative w-8 h-8 flex-shrink-0" aria-hidden="true">
          <div className="w-8 h-8 bg-green dark:bg-green-dark rounded-full flex items-center justify-center">
            <FaMicrophone className="w-4 h-4 text-gray-900 dark:text-gray-100" />
          </div>
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-gray-700 dark:text-gray-200 font-semibold leading-snug flex items-center gap-2">
              <span className="inline-flex w-2 h-2 rounded-full bg-green dark:bg-green-dark animate-pulse" />
              Listening...
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {languageLabel}
            </p>
          </div>

          <div className="flex items-start justify-end gap-2 flex-nowrap">
            {typeof onStop === "function" && (
              <button
                type="button"
                onClick={onStop}
                className={STOP_BUTTON_CLASS}
                title="Stop recording"
                aria-label="Stop recording"
              >
                Stop
              </button>
            )}

            <div className="relative group inline-flex">
              <span
                className={getModePillClass(isCommandMode)}
                aria-label={isCommandMode ? "Command mode" : "Dictation mode"}
                aria-describedby={tooltipId}
                tabIndex={0}
              >
                {modeLabel}
              </span>
              {variant === "sidebar" ? (
                <SidebarHotkeysTooltip id={tooltipId} />
              ) : (
                <EditorHotkeysTooltip id={tooltipId} />
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
          {transcriptText ? (
            <p className="text-sm text-gray-700 dark:text-gray-200 italic break-words whitespace-pre-wrap max-h-28 overflow-y-auto">
              {transcriptText}
            </p>
          ) : (
            <p className="text-sm text-gray-500 animate-pulse dark:text-gray-400">
              {helpText}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RecordingMicPopover;
